import { UserModel, UserRef } from '$tablet-app/src/app/auth/UserModel';
import { color } from '$tablet-app/src/app/theme';
import { User } from '@akin-home/amplify';
import React, { FC } from 'react';
import { TextStyle, View, ViewStyle } from 'react-native';
import { ProfileIcon } from '../ProfileIcon/ProfileIcon';
import Spacer from '../Spacer';
import { Text } from '../Text/Text';

interface ProfileIconsProps {
  profiles: (User | UserRef | UserModel)[];
  viewStyle?: ViewStyle;
  profileIconStyle?: ViewStyle;
  preset?: ProfileIconsPresetNames;
  displayCount?: number;
}

/** Displays a list of profile icons for the given user profiles */
const ProfileIcons: FC<ProfileIconsProps> = (props) => {
  // Display the view as overlapping or not overalapping, depending on the preset show, and set the border color to
  // the value passed for the color preset
  const preset = props.preset ? presets[props.preset] : presets.default;

  const viewStyle = { ...preset.view, ...props.viewStyle };
  const profileIconStyle = { ...preset.profileIcon, ...props.profileIconStyle };

  const { profiles, extraCount } = getProfileIconsData({
    profiles: props.profiles,
    displayCount: props.displayCount,
  });

  return (
    <View style={viewStyle}>
      {/* Overlay right reverses order */}
      {extraCount > 0 && props.preset === 'overlapRight' ? (
        <ExtraCountToken count={extraCount} />
      ) : null}

      {profiles.map((profile) => (
        <ProfileIcon
          key={profile.id}
          profile={profile}
          style={profileIconStyle}
        />
      ))}

      {extraCount > 0 && props.preset !== 'overlapRight' ? (
        <ExtraCountToken count={extraCount} />
      ) : null}

      {props.preset === 'overlapRight' && profiles.length ? (
        <Spacer width="20px" />
      ) : null}
    </View>
  );
};

const ExtraCountToken = ({ count }: { count: number }) => (
  <View style={extraCountContainer}>
    <Text style={extraCountText}>+{count}</Text>
  </View>
);

const commonViewStyles: ViewStyle = {
  alignItems: 'center',
};

const extraCountContainer: ViewStyle = {
  backgroundColor: color.background,
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: 50,
  height: 20,
  width: 20,
  marginLeft: -12,
  zIndex: 100,
};

const extraCountText: TextStyle = {
  fontSize: 10,
  fontWeight: 'bold',
};

// preset/component/styles
export const presets: Record<string, Record<string, ViewStyle>> = {
  // preset
  overlapRight: {
    view: { ...commonViewStyles, flexDirection: 'row-reverse' },
    profileIcon: { marginLeft: -20 },
  },
  overlapLeft: {
    view: { ...commonViewStyles, flexDirection: 'row' },
    profileIcon: { marginLeft: -20 },
  },
  default: {
    view: { ...commonViewStyles, flexDirection: 'row' },
    profileIcon: {},
  },
};

export type ProfileIconsPresetNames = keyof typeof presets;

const getProfileIconsData = ({
  profiles,
  displayCount,
}: {
  profiles: User[];
  displayCount?: number;
}) => {
  if (displayCount && displayCount < profiles.length) {
    return {
      profiles: profiles.slice(0, displayCount),
      extraCount: profiles.length - displayCount,
    };
  }

  return {
    profiles,
    extraCount: 0,
  };
};

const memoProfile = React.memo(ProfileIcons);

export { memoProfile as ProfileIcons };
